import React, { useState } from 'react';
import { Button, notification, Spin, Upload } from 'antd';
import { DeleteOutlined, InboxOutlined, LoadingOutlined } from '@ant-design/icons';
import '@google/model-viewer';
import { getStorageService } from '../storage/storage';
import { useTranslation } from 'react-i18next';

const { Dragger } = Upload;

// Single-slot GLB (3D model) upload + inline viewer. Reuses the same generic
// uploadDocument()/deleteFiles() storage calls as DocumentUploader - GLB is
// just another binary file as far as storage is concerned.
const GlbUploader = ({ disabledAdd, storageFolder, fileList, setFileList }) => {
  const { t } = useTranslation();
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const current = fileList && fileList[0];

  const storageService = getStorageService(storageFolder);

  const beforeUpload = (file) => {
    const isGlb = file.name.toLowerCase().endsWith('.glb');
    if (!isGlb) {
      notification.error({
        message: t('glbUploader.invalidFileType', 'Invalid file type'),
        description: t('glbUploader.onlyGlbAllowed', 'Only .glb files are allowed.'),
      });
    }
    return isGlb || Upload.LIST_IGNORE;
  };

  const customUploadRequest = async (e) => {
    if (!e.file) return;
    setUploadInProgress(true);
    try {
      const { originalUrl } = await storageService.uploadDocument(e.file);
      setFileList([{
        uid: e.file.uid,
        name: e.file.name,
        url: originalUrl,
      }]);
    } catch (error) {
      notification.error({
        message: t('documentUploader.uploadFailed'),
        description: t('documentUploader.uploadFailed'),
      });
    } finally {
      setUploadInProgress(false);
    }
  };

  const onRemove = async () => {
    if (!current) return;
    try {
      await storageService.deleteFiles(current.name);
    } catch (error) {
      // ponytail: best-effort delete, same as DocumentUploader - storage cleanup
      // failure shouldn't block clearing the field locally.
    } finally {
      setFileList([]);
    }
  };

  if (current) {
    return (
      <div>
        {/* eslint-disable-next-line react/no-unknown-property */}
        <model-viewer
          src={current.url}
          camera-controls="true"
          auto-rotate="true"
          style={{ width: '100%', height: 300, background: '#fafafa', borderRadius: 6 }}
        />
        {!disabledAdd && (
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={onRemove}
            style={{ marginTop: 8 }}
          >
            {t('glbUploader.remove', 'Remove 3D Model')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <Dragger
      customRequest={customUploadRequest}
      beforeUpload={beforeUpload}
      showUploadList={false}
      disabled={disabledAdd}
      accept=".glb"
    >
      <p className="ant-upload-drag-icon">
        {uploadInProgress
          ? <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
          : <InboxOutlined />}
      </p>
      <p className="ant-upload-text">{t('glbUploader.selectGlb', 'Click or drag a .glb file to upload')}</p>
    </Dragger>
  );
};

export default GlbUploader;
